import React, { useContext, useEffect, useRef, useState } from "react";
import {
    SUBSCRIBE_TO_TICKET,
    TICKET_PHASE_CHANGE,
} from "services/socket/events";
import { globalStatusCategories, ticketsPhases } from "./enum";
import { useSocket } from "services/socket/hook";
import { Dropdown } from "components/ui";
import API from "services/lib/api";
import { apiRoute } from "services";
import { getErrorMessage } from "utils/helper";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import "./TicketStatus.scss";

const CLEAR_STATUS = "__CLEAR_CUSTOM_STATUS__";

const StatusPill = ({ fillColor, title }) => (
    <div className='ticket__status'>
        <div className='status__circle' style={{ background: fillColor }}></div>
        <span>{title} </span>
    </div>
);

const TicketStatus = ({ ticketPhase, ticketId }) => {
    const [setPhase, updatePhase] = useState(ticketPhase);
    const [customStatus, setCustomStatus] = useState(null);
    const [statusOptions, setStatusOptions] = useState([]);
    const [updating, setUpdating] = useState(false);

    const currentPhase = ticketsPhases[setPhase];
    const socket = useSocket();
    const toastMessage = useContext(ToastContext);
    const isMounted = useRef(true);

    const handleNewPhase = (data) => {
        updatePhase(data?.ticketPhase);
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        socket?.emit?.(SUBSCRIBE_TO_TICKET, { ticketId });
        socket?.on?.(TICKET_PHASE_CHANGE, handleNewPhase);
        return () => {
            socket?.off?.(TICKET_PHASE_CHANGE);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId]);

    useEffect(() => {
        updatePhase(ticketPhase);
    }, [ticketPhase]);

    useEffect(() => {
        if (!ticketId) return;

        const fetchCustomStatuses = async () => {
            try {
                const res = await API.get(
                    apiRoute.getTicketCustomStatus(ticketId)
                );
                if (res.status === 200 && isMounted.current) {
                    const { current, options } = res?.data?.data || {};
                    setCustomStatus(current || null);
                    setStatusOptions(options || []);
                }
            } catch (err) {
                // The pill falls back to the ticket phase when statuses can't
                // be loaded, so this stays silent.
                if (isMounted.current) {
                    setCustomStatus(null);
                    setStatusOptions([]);
                }
            }
        };

        fetchCustomStatuses();
        // Statuses are scoped to the phase, so refetch whenever it moves.
    }, [ticketId, setPhase]);

    const assignCustomStatus = async (customTicketStatusId) => {
        if (updating) return;

        setUpdating(true);
        try {
            const res = await API.put(
                apiRoute.assignTicketCustomStatus(ticketId),
                { customTicketStatusId }
            );

            if (res.status === 200 && isMounted.current) {
                const { message, data } = res?.data || {};
                setCustomStatus(data?.current || null);
                setStatusOptions(data?.options || []);
                if (data?.ticketPhase) updatePhase(data.ticketPhase);
                toastMessage(message);
            }
        } catch (err) {
            toastMessage(getErrorMessage(err));
        } finally {
            if (isMounted.current) setUpdating(false);
        }
    };

    const handleChange = ({ value }) => {
        if (value === customStatus?.customTicketStatusId) return;
        assignCustomStatus(value === CLEAR_STATUS ? null : value);
    };

    const activeCategory =
        globalStatusCategories[customStatus?.globalCategory] || null;
    const fillColor = activeCategory?.fillColor || currentPhase?.fillColor;
    const title = customStatus?.name || currentPhase?.title;

    // Nothing to pick from — keep the plain, read-only pill.
    if (!statusOptions?.length) {
        return <StatusPill fillColor={fillColor} title={title} />;
    }

    const options = statusOptions.map((status) => ({
        value: status.customTicketStatusId,
        label: (
            <span className='ticket__status__option'>
                <span
                    className='status__circle'
                    style={{
                        background:
                            globalStatusCategories[status.globalCategory]
                                ?.fillColor,
                    }}></span>
                <span className='ticket__status__option--name'>
                    {status.name}
                </span>
            </span>
        ),
    }));

    if (customStatus) {
        options.push({
            value: CLEAR_STATUS,
            label: (
                <span className='ticket__status__option ticket__status__option--clear'>
                    Clear status
                </span>
            ),
        });
    }

    return (
        <Dropdown
            placeholder={<StatusPill fillColor={fillColor} title={title} />}
            className='ticket__status__dropdown'
            ddOptionsClass='ticket__status__options'
            menuAlign='start'
            anchor={false}
            changeValue={false}
            ddValue={{ value: customStatus?.customTicketStatusId || "" }}
            onChange={handleChange}
            options={options}
        />
    );
};

export default TicketStatus;
